/**
 * Sales Order Screen (6.2.1)
 * POS Module - Orders
 */
import React, { useState, useCallback, useEffect } from 'react';
import { FileText, Edit2, Trash2, Plus, Save, RefreshCw, Download, Printer, X, Search, ShoppingCart, Minus } from 'lucide-react';
import { salesOrder } from '@/services/api/posService';

const SCREEN_ID = '6.2.1';

const SalesOrderScreen = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form state
  const [form, setForm] = useState({
    SONo: '',
    SODate: new Date().toISOString().split('T')[0],
    SORef: '',
    SalesAccount: '',
    Customer: '',
    CustomerId: '',
    GSTIN: '',
    StCode: '',
    OS: 0,
    CreditLimit: 0,
    Items: [],
    Freight: 0,
    PackingCharges: 0,
    Discount: 0,
    GrossAmount: 0,
    NetAmount: 0,
    Remarks: '',
    Status: 'Pending'
  });
  
  // New item form
  const [newItem, setNewItem] = useState({
    ItemCode: '',
    HSNCode: '',
    ItemName: '',
    Qty: 1,
    Rate: 0,
    MRP: 0,
    Discount: 0,
    Value: 0
  });

  // Mock customers & items
  const [customers] = useState([
    { id: 'CUS001', name: 'ABC Enterprises', gstin: '27AABCU9603R1ZM' },
    { id: 'CUS002', name: 'XYZ Retail', gstin: '07AADCB2230M1ZP' },
  ]);

  // Load data
  const handleLoadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await salesOrder.getAll();
      setData(res.data?.data || []);
    } catch (e) {
      // Mock data
      setData([
        { SONo: 'SO/2024/001', SODate: '2024-03-14', Customer: 'ABC Enterprises', Items: 3, GrossAmount: 15000, NetAmount: 14250, Status: 'Pending' },
        { SONo: 'SO/2024/002', SODate: '2024-03-13', Customer: 'XYZ Retail', Items: 5, GrossAmount: 28000, NetAmount: 26600, Status: 'Confirmed' },
        { SONo: 'SO/2024/003', SODate: '2024-03-12', Customer: 'PQR Trading', Items: 2, GrossAmount: 8500, NetAmount: 8500, Status: 'Delivered' },
      ]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    handleLoadData();
  }, [handleLoadData]);

  // Calculate totals
  const calculateTotals = (items) => {
    const gross = items.reduce((sum, item) => sum + item.Value, 0);
    const discount = form.Discount || 0;
    const freight = form.Freight || 0;
    const packing = form.PackingCharges || 0;
    const net = gross - discount + freight + packing;
    return { GrossAmount: gross, NetAmount: net };
  };

  // Add item
  const handleAddItem = () => {
    if (!newItem.ItemCode || newItem.Qty <= 0) {
      alert('Item Code and Quantity are required');
      return;
    }
    const value = (newItem.Rate * newItem.Qty) - newItem.Discount;
    const item = { ...newItem, Value: value };
    const updatedItems = [...form.Items, item];
    const totals = calculateTotals(updatedItems);
    setForm(p => ({ ...p, Items: updatedItems, ...totals }));
    setNewItem({ ItemCode: '', HSNCode: '', ItemName: '', Qty: 1, Rate: 0, MRP: 0, Discount: 0, Value: 0 });
  };

  // Remove item
  const handleRemoveItem = (index) => {
    const updatedItems = form.Items.filter((_, i) => i !== index);
    const totals = calculateTotals(updatedItems);
    setForm(p => ({ ...p, Items: updatedItems, ...totals }));
  };

  // Save
  const handleSave = async () => {
    if (!form.Customer || form.Items.length === 0) {
      alert('Customer and at least one item are required');
      return;
    }
    
    if (editItem) {
      setData(prev => prev.map(d => d.SONo === editItem.SONo ? {...d, ...form, Items: form.Items.length} : d));
    } else {
      const newSONo = `SO/2024/${String(data.length + 1).padStart(3, '0')}`;
      setData(prev => [...prev, { ...form, SONo: newSONo, Items: form.Items.length }]);
    }
    clearForm();
    setShowForm(false);
  };

  // Delete
  const handleDelete = async () => {
    if (!window.confirm(`Delete ${selectedItems.length} order(s)?`)) return;
    setData(prev => prev.filter(d => !selectedItems.includes(d.SONo)));
    setSelectedItems([]);
  };

  const clearForm = () => {
    setForm({
      SONo: '', SODate: new Date().toISOString().split('T')[0], SORef: '', SalesAccount: '',
      Customer: '', CustomerId: '', GSTIN: '', StCode: '', OS: 0, CreditLimit: 0,
      Items: [], Freight: 0, PackingCharges: 0, Discount: 0, GrossAmount: 0, NetAmount: 0,
      Remarks: '', Status: 'Pending'
    });
    setEditItem(null);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({ ...form, ...item, Items: [] });
    setShowForm(true);
  };

  const toggleSelect = (id) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setForm(p => ({
      ...p,
      CustomerId: customerId,
      Customer: customer?.name || '',
      GSTIN: customer?.gstin || ''
    }));
  };

  const filteredData = data.filter(d => 
    d.Customer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.SONo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-500/10 text-yellow-500';
      case 'Confirmed': return 'bg-blue-500/10 text-blue-500';
      case 'Delivered': return 'bg-green-500/10 text-green-500';
      case 'Cancelled': return 'bg-red-500/10 text-red-500';
      default: return 'bg-slate-500/10 text-slate-500';
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);

  const inputCls = `w-full px-3 py-2 rounded border text-sm ${isDark ? 'bg-[#111] border-[#333] text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const labelCls = `block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`;
  const btnCls = `px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 ${isDark ? 'bg-[#1a1a1e] text-slate-300 hover:bg-[#252528]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`;

  return (
    <div className={`h-full flex flex-col ${isDark ? 'bg-[#0a0a0c]' : 'bg-gray-50'}`} data-testid="sales-order-screen">
      {/* Header */}
      <div className="px-4 py-2 bg-blue-600 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShoppingCart size={18} />
          <span className="font-semibold text-sm">Sales Order</span>
          <span className="text-[10px] px-2 py-0.5 rounded bg-white/20">{SCREEN_ID}</span>
        </div>
        <span className="text-[10px] opacity-75">TRANSACTION</span>
      </div>

      {/* Toolbar */}
      <div className={`px-3 py-2 border-b flex items-center justify-between ${isDark ? 'border-[#333] bg-[#0e0e10]' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-center gap-2">
          <button onClick={() => { clearForm(); setShowForm(true); }} className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium flex items-center gap-1.5">
            <Plus size={12} /> New Order
          </button>
          {selectedItems.length > 0 && (
            <button onClick={handleDelete} className="px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium flex items-center gap-1.5">
              <Trash2 size={12} /> Delete ({selectedItems.length})
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..." className={`pl-7 pr-3 py-1.5 rounded border text-xs w-48 ${isDark ? 'bg-[#111] border-[#333] text-white' : 'bg-white border-gray-300'}`} />
          </div>
          <button onClick={handleLoadData} disabled={loading} className={btnCls}>
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button className={btnCls}><Download size={12} /> Export</button>
          <button className={btnCls}><Printer size={12} /> Print</button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Data Grid */}
        <div className={`flex-1 overflow-auto ${showForm ? 'border-r' : ''} ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
          <table className="w-full text-xs">
            <thead className={`sticky top-0 z-10 ${isDark ? 'bg-[#0e0e10]' : 'bg-gray-50'}`}>
              <tr className={`border-b ${isDark ? 'border-[#222]' : 'border-gray-200'}`}>
                <th className="px-2 py-2 w-8">
                  <input type="checkbox" onChange={(e) => setSelectedItems(e.target.checked ? data.map(d => d.SONo) : [])}
                    checked={selectedItems.length === data.length && data.length > 0} className="accent-blue-500" />
                </th>
                <th className={`px-3 py-2 text-left text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>S.O No</th>
                <th className={`px-3 py-2 text-left text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Date</th>
                <th className={`px-3 py-2 text-left text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Customer</th>
                <th className={`px-3 py-2 text-center text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Items</th>
                <th className={`px-3 py-2 text-right text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Gross Amount</th>
                <th className={`px-3 py-2 text-right text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Net Amount</th>
                <th className={`px-3 py-2 text-left text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Status</th>
                <th className={`px-3 py-2 text-center text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">Loading...</td></tr>
              ) : filteredData.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">No orders found.</td></tr>
              ) : filteredData.map((r) => (
                <tr key={r.SONo} className={`border-b cursor-pointer transition-colors ${selectedItems.includes(r.SONo) ? isDark ? 'bg-blue-900/20' : 'bg-blue-50' : isDark ? 'border-[#1a1a1e] hover:bg-[#111]' : 'border-gray-100 hover:bg-gray-50'}`} onClick={() => openEdit(r)}>
                  <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedItems.includes(r.SONo)} onChange={() => toggleSelect(r.SONo)} className="accent-blue-500" />
                  </td>
                  <td className={`px-3 py-1.5 font-mono font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{r.SONo}</td>
                  <td className={`px-3 py-1.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{r.SODate}</td>
                  <td className={`px-3 py-1.5 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{r.Customer}</td>
                  <td className={`px-3 py-1.5 text-center ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{r.Items}</td>
                  <td className={`px-3 py-1.5 text-right font-mono ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{formatCurrency(r.GrossAmount)}</td>
                  <td className={`px-3 py-1.5 text-right font-mono font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(r.NetAmount)}</td>
                  <td className="px-3 py-1.5"><span className={`px-2 py-0.5 rounded text-[10px] font-medium ${getStatusColor(r.Status)}`}>{r.Status}</span></td>
                  <td className="px-3 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => openEdit(r)} className={`p-1 rounded ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`}><Edit2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Form Panel */}
        {showForm && (
          <div className={`w-[500px] flex flex-col ${isDark ? 'bg-[#0a0a0c]' : 'bg-gray-50'}`}>
            <div className="flex items-center justify-between p-4 pb-2">
              <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{editItem ? 'Edit Order' : 'New Sales Order'}</h3>
              <button onClick={() => { setShowForm(false); clearForm(); }} className={`p-1 rounded ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-gray-200 text-gray-500'}`}><X size={16} /></button>
            </div>

            <div className="flex-1 overflow-auto p-4 pt-2">
              {/* Header Section */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div><label className={labelCls}>S.O No</label><input value={form.SONo} readOnly placeholder="Auto-generated" className={`${inputCls} ${isDark ? 'bg-[#1a1a1e]' : 'bg-gray-100'}`} /></div>
                <div><label className={labelCls}>S.O Date</label><input type="date" value={form.SODate} onChange={(e) => setForm(p => ({...p, SODate: e.target.value}))} className={inputCls} /></div>
                <div className="col-span-2"><label className={labelCls}>Customer <span className="text-red-500">*</span></label>
                  <select value={form.CustomerId} onChange={(e) => handleCustomerChange(e.target.value)} className={inputCls}>
                    <option value="">-- Select Customer --</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div><label className={labelCls}>GSTIN</label><input value={form.GSTIN} readOnly className={`${inputCls} ${isDark ? 'bg-[#1a1a1e]' : 'bg-gray-100'}`} /></div>
                <div><label className={labelCls}>S.O. Ref</label><input value={form.SORef} onChange={(e) => setForm(p => ({...p, SORef: e.target.value}))} className={inputCls} /></div>
              </div>

              {/* Items Section */}
              <div className={`border rounded p-3 mb-4 ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
                <div className="text-xs font-semibold mb-2">Add Items</div>
                <div className="grid grid-cols-5 gap-2 mb-2">
                  <input placeholder="Item Code" value={newItem.ItemCode} onChange={(e) => setNewItem(p => ({...p, ItemCode: e.target.value}))} className={`${inputCls} text-xs`} />
                  <input placeholder="Item Name" value={newItem.ItemName} onChange={(e) => setNewItem(p => ({...p, ItemName: e.target.value}))} className={`col-span-2 ${inputCls} text-xs`} />
                  <input type="number" placeholder="Qty" value={newItem.Qty} onChange={(e) => setNewItem(p => ({...p, Qty: parseFloat(e.target.value) || 0}))} className={`${inputCls} text-xs`} />
                  <input type="number" placeholder="Rate" value={newItem.Rate} onChange={(e) => setNewItem(p => ({...p, Rate: parseFloat(e.target.value) || 0}))} className={`${inputCls} text-xs`} />
                </div>
                <button onClick={handleAddItem} className="w-full py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium flex items-center justify-center gap-1">
                  <Plus size={12} /> Add Item
                </button>
                
                {/* Items List */}
                {form.Items.length > 0 && (
                  <div className="mt-3">
                    <table className="w-full text-xs">
                      <thead className={isDark ? 'bg-[#1a1a1e]' : 'bg-gray-100'}>
                        <tr>
                          <th className="px-2 py-1 text-left">Item</th>
                          <th className="px-2 py-1 text-right">Qty</th>
                          <th className="px-2 py-1 text-right">Rate</th>
                          <th className="px-2 py-1 text-right">Value</th>
                          <th className="px-2 py-1 w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.Items.map((item, i) => (
                          <tr key={i} className={`border-t ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
                            <td className="px-2 py-1">{item.ItemCode} - {item.ItemName}</td>
                            <td className="px-2 py-1 text-right">{item.Qty}</td>
                            <td className="px-2 py-1 text-right">{item.Rate}</td>
                            <td className="px-2 py-1 text-right font-medium">{item.Value.toFixed(2)}</td>
                            <td className="px-2 py-1"><button onClick={() => handleRemoveItem(i)} className="text-red-500 hover:text-red-400"><X size={12} /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Totals */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div><label className={labelCls}>Freight</label><input type="number" value={form.Freight} onChange={(e) => setForm(p => ({...p, Freight: parseFloat(e.target.value) || 0}))} className={inputCls} /></div>
                <div><label className={labelCls}>Packing Charges</label><input type="number" value={form.PackingCharges} onChange={(e) => setForm(p => ({...p, PackingCharges: parseFloat(e.target.value) || 0}))} className={inputCls} /></div>
                <div><label className={labelCls}>Discount</label><input type="number" value={form.Discount} onChange={(e) => setForm(p => ({...p, Discount: parseFloat(e.target.value) || 0}))} className={inputCls} /></div>
                <div><label className={labelCls}>Gross Amount</label><input value={formatCurrency(form.GrossAmount)} readOnly className={`${inputCls} ${isDark ? 'bg-[#1a1a1e]' : 'bg-gray-100'}`} /></div>
              </div>

              <div className={`p-3 rounded ${isDark ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Net Amount</span>
                  <span className="text-xl font-bold text-blue-500">{formatCurrency(form.NetAmount)}</span>
                </div>
              </div>
            </div>

            <div className={`flex gap-2 p-4 border-t ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
              <button onClick={handleSave} className="flex-1 px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium flex items-center justify-center gap-1">
                <Save size={12} /> {editItem ? 'Update Order' : 'Create Order'}
              </button>
              <button onClick={clearForm} className={`px-3 py-2 rounded text-xs font-medium ${isDark ? 'bg-[#1a1a1e] text-slate-300' : 'bg-gray-200 text-gray-700'}`}>Clear</button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={`px-4 py-1.5 border-t text-[10px] flex items-center justify-between ${isDark ? 'border-[#333] bg-[#0e0e10] text-slate-500' : 'border-gray-200 bg-gray-50 text-gray-500'}`}>
        <span>{filteredData.length} orders</span>
        <span>Screen: {SCREEN_ID}</span>
      </div>
    </div>
  );
};

export default SalesOrderScreen;
